'use client';

import { useCallback, useState } from 'react';
import config from 'config';
import { ROUTES } from 'constant';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

import {
  Box,
  Button,
  Flex,
  Stack,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { LocationFlowType, LocationInterface, LocationPickType } from 'types';

import {
  FavoritesLocation,
  LocationContainer,
  NearMeLocation
} from 'components/Locations';
import { MultiStepHeader } from 'ui';

const {
  booking: { location },
  shop: {
    packages: { location: packagesLocation },
    memberships: { location: membershipsLocation }
  }
} = config;

const getContent = (flow: LocationFlowType) => {
  switch (flow) {
    case LocationFlowType.BOOK:
      return {
        title: location.title,
        subtitle: location.description,
        paddings: location.paddings
      };
    case LocationFlowType.PACKAGES:
      return {
        title: packagesLocation.heading,
        subtitle: packagesLocation.subtitle,
        paddings: packagesLocation.paddings
      };
    case LocationFlowType.MEMBERSHIPS:
      return {
        title: membershipsLocation.title,
        subtitle: membershipsLocation.subtitle,
        paddings: membershipsLocation.paddings
      };
    default:
      return {
        title: '',
        subtitle: '',
        paddings: {}
      };
  }
};

interface LocationStepProps {
  locationFlow: LocationFlowType;
  onSelectLocation: (location: LocationInterface) => void;
  onSkip: () => void;
  scrollIntoView?: () => void;
}

const LocationStep = ({
  locationFlow,
  onSelectLocation,
  onSkip,
  scrollIntoView = () => {}
}: LocationStepProps) => {
  const {
    colors: {
      palette: {
        components: { locations: styles }
      }
    }
  } = useTheme();
  const router = useRouter();
  const { selectedLocation } = useAppSelector((state) => state.user);
  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const [locationPickType, setLocationPickType] = useState<LocationPickType>(
    LocationPickType.LIST
  );
  const isBookingFlow = locationFlow === LocationFlowType.BOOK;
  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: 'md' }
  );

  const { title, subtitle, paddings } = getContent(locationFlow);

  const onListType = useCallback(() => {
    setLocationPickType(LocationPickType.LIST);
    scrollIntoView();
  }, [scrollIntoView]);

  const onNearMeClick = useCallback(() => {
    if (isLogged) {
      setLocationPickType(LocationPickType.NEAR_ME);
      scrollIntoView();
    } else {
      router.push(ROUTES.login);
    }
  }, [scrollIntoView, isLogged, router]);

  const onFavotiresClick = useCallback(() => {
    if (isLogged) {
      setLocationPickType(LocationPickType.FAVORITES);
      scrollIntoView();
    } else {
      router.push(ROUTES.login);
    }
  }, [scrollIntoView, isLogged, router]);

  const renderLocationType = () => {
    switch (locationPickType) {
      case LocationPickType.NEAR_ME:
        return (
          <NearMeLocation
            onSelectHandler={onSelectLocation}
            onBackHandler={onListType}
          />
        );
      case LocationPickType.FAVORITES:
        return (
          <FavoritesLocation
            onSelectHandler={onSelectLocation}
            onBackHandler={onListType}
          />
        );
      default:
        return (
          <>
            <MultiStepHeader
              title={title}
              subTitle={subtitle}
              headingColor={styles.heading}
              descriptionColor={styles.subtitle}
              mt={0}
              mb={{ base: '20px', md: '40px' }}
              descriptionPX={paddings}
            />
            <LocationContainer
              handleSelectLocation={onSelectLocation}
              onNearMeHandler={onNearMeClick}
              onFavotiresHandler={onFavotiresClick}
            />
            <Flex justifyContent='center' mt='50px'>
              <Button
                onClick={onSkip}
                variant='outline'
                width='150px'
                h='45px'
                isDisabled={!selectedLocation?.id}
              >
                Back
              </Button>
            </Flex>
          </>
        );
    }
  };

  const banner = isMobile ? location.bannerMobile : location.banner;

  return (
    <Box w='full'>
      {isBookingFlow &&
        locationPickType === LocationPickType.LIST &&
        banner && (
          <Box mx={{ base: '-15px', md: 0 }} mb='20px'>
            <Image
              src={isMobile ? location.bannerMobile : location.banner}
              alt='shop-banner'
              priority
              width={1120}
              height={378}
              style={{
                width: '100%',
                height: '100%'
              }}
            />
          </Box>
        )}

      <Stack
        maxW='1200px'
        w='100%'
        justifyContent='center'
        alignItems='center'
        mx='auto'
        gap={0}
      >
        {renderLocationType()}
      </Stack>
    </Box>
  );
};

export default LocationStep;
